/**
 * incidenciasListaRaya.js
 *
 * Traduce `LineaPrenomina.incidencias[]` a la rejilla de dias que pinta la Lista de Raya:
 * una columna por dia del periodo con un marcador por empleado, mas el total de faltas.
 *
 * Modulo PURO: no importa React, ni axios, ni xlsx. Se puede verificar con un `node script.js`
 * desechable, igual que `construirLibroListaRaya`.
 *
 * LO QUE ESTE MODULO **NO** HACE
 * ------------------------------
 * No decide si un tipo de incidencia es una falta, un permiso, una incapacidad o vacaciones. Esa
 * regla (`seConsidera` cruzado con `derechoSueldo`) ya vive dos veces en el backend —inline en
 * `sumaIncidencia` del motor y replicada en `Utils/incidenciasReporte.utils.js`, que existe
 * justamente "para que el reporte no pueda contradecir al recibo"—. Una tercera copia aqui seria
 * la que empieza a divergir.
 *
 * En vez de eso, `selectTodasTiposIncidencias` publica `cubeta` y `unidad` ya calculadas en cada
 * tipo del catalogo, y este modulo solo las LEE. Si `cubeta` llega `undefined` en todos los tipos,
 * lo que falta es esa parte del backend.
 *
 * LAS CUATRO REGLAS DE CONTEO
 * ---------------------------
 * Portadas del Reporte de Incidencias (`reportesNomina.controllers.js`, bloque de comentarios
 * sobre `reporteIncidencias`), porque los dos numeros tienen que coincidir:
 *
 * 1. `diaPeriodo < 1` NO es un dia. Es el marcador de totales sin posicion: el bloque
 *    `[vac-inject]` del calculo guarda un respaldo con `diaPeriodo: 0` que lleva el TOTAL de dias
 *    del movimiento de vacaciones. Sumarlo junto a los dias repartidos las duplica. (Ojo: el motor
 *    hace lo contrario a proposito — `enRelacion` lo deja pasar SIEMPRE.)
 * 2. Se recorta por la ventana de relacion laboral. El reloj checador marca ausencia en todo dia
 *    con horario sin checada y no mira `fechaBaja`, asi que a un dado de baja a media semana le
 *    inventa faltas hasta el domingo. La ventana ya llega por linea en `posInicioRelacion` /
 *    `posFinRelacion`, calculada por `datosPrenomina` con el mismo helper que usa el motor.
 * 3. Un dia con `entradas: []` es el marcador de "revisado, sin incidencia", no un cero: la celda
 *    va vacia.
 * 4. La `cantidad` de una entrada no siempre son dias — los retardos y las horas extra son HORAS.
 *    Por eso solo las faltas se pintan como numero y solo ellas entran al TOTAL; lo demas se
 *    rotula con su clave.
 */

// Cubeta de las ausencias que descuentan sueldo. Es la unica que se pinta como numero, porque es
// la unica que el TOTAL suma (igual que el =SUM() del archivo que lleva el cliente a mano, que
// ignora el texto de las demas celdas).
export const CUBETA_FALTAS = 'faltas'

const DIAS_SEMANA = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB']

const MS_DIA = 86400000

/**
 * Strings que el motor escribe (o escribia) en `entrada.tipo` cuando no resolvia un
 * TipoIncidencia real. Espejo de `ALIAS_SEMANTICO` en `Utils/incidenciasReporte.utils.js`.
 *
 * No se pueden mapear a un documento del catalogo —¿cual de las tres claves de falta seria
 * 'falta'?— pero si se sabe a que cubeta van. 'permiso_sin_goce' cae en faltas porque es donde lo
 * deja el motor: esos dias se cuentan dentro de FALTAS.
 */
export const ALIAS_SEMANTICO = {
  vacacion: { cubeta: 'vacaciones', unidad: 'dias', nombre: 'Vacaciones', clave: 'VAC' },
  incapacidad: { cubeta: 'incapacidades', unidad: 'dias', nombre: 'Incapacidad', clave: 'INC' },
  falta: { cubeta: 'faltas', unidad: 'dias', nombre: 'Falta', clave: 'FAL' },
  permiso_goce: { cubeta: 'permisoGoce', unidad: 'dias', nombre: 'Permiso con goce', clave: 'PCG' },
  permiso_sin_goce: { cubeta: 'faltas', unidad: 'dias', nombre: 'Permiso sin goce', clave: 'PSG' },
  retardo: { cubeta: 'retardos', unidad: 'horas', nombre: 'Retardo', clave: 'RET' },
  hora_extra: { cubeta: 'horasExtra', unidad: 'horas', nombre: 'Hora extra', clave: 'HE' },
  prima_dominical: { cubeta: null, unidad: 'dias', nombre: 'Prima dominical', clave: 'PD' },
}

// Las claves se comparan en mayusculas y sin espacios: el catalogo las captura a mano y
// `entrada.tipo` es una copia hecha al guardar, asi que un ' finj ' es posible.
const normalizarClave = (clave) =>
  typeof clave === 'string' ? clave.trim().toUpperCase() : ''

/**
 * Lee el dia de calendario de una fecha **en UTC** y lo ancla al mediodia UTC.
 *
 * NO usa `dayjs(fecha)` a secas, y la diferencia es un bug real: dayjs resuelve la fecha en la
 * zona del navegador, asi que una `fechaInicio` guardada a las 00:00Z —la forma que produce el
 * importador de Excel— sale como el dia ANTERIOR en Mexico. Leyendo en UTC el dia sale bien en las
 * tres formas en que conviven estas fechas: 12:00Z (fecha-dia anclada), 00:00Z (importacion) y
 * 06:00Z (medianoche de Mexico, DatePicker).
 *
 * Mismo criterio que `anclarDesdeUtc` en `erp_backend/Utils/incidenciasReporte.utils.js`.
 *
 * @returns {number|null} timestamp anclado al mediodia UTC
 */
export function anclarDesdeUtc(fecha) {
  if (!fecha) return null
  if (typeof fecha === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha)
    if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)
  }
  const d = fecha instanceof Date ? fecha : new Date(fecha)
  if (Number.isNaN(d.getTime())) return null
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12)
}

/**
 * Indice del catalogo para resolver entradas. Se construye UNA vez por descarga.
 *
 * `semanticos` resuelve los alias legado contra un documento real del catalogo cuando existe, para
 * que una vacacion vieja caiga en la MISMA columna que una nueva en vez de abrir una duplicada.
 *
 * El catalogo tiene que venir SIN filtrar por `estado`: un tipo deshabilitado hoy tiene entradas
 * historicas que siguen siendo reales. `selectTodasTiposIncidencias` sin `valor` ya se comporta asi.
 */
export function construirIndiceTipos(tipos) {
  const porId = new Map()
  const porClave = new Map()

  for (const tipo of tipos || []) {
    if (tipo?._id) porId.set(String(tipo._id), tipo)
    const clave = normalizarClave(tipo?.clave)
    // La primera gana: el catalogo no tiene indice unico sobre `clave`, y si dos tipos la
    // comparten conviene quedarse con uno en vez de alternar entre descargas.
    if (clave && !porClave.has(clave)) porClave.set(clave, tipo)
  }

  const buscar = (predicado) => [...porId.values()].find(predicado) || null
  const semanticos = {
    vacacion:
      buscar((t) => t.esVacaciones === true) ||
      buscar((t) => t.cubeta === 'vacaciones') ||
      porClave.get('VAC') ||
      null,
    incapacidad: porClave.get('INC') || buscar((t) => t.cubeta === 'incapacidades') || null,
    retardo: porClave.get('RET') || null,
  }

  return { porId, porClave, semanticos }
}

const descriptorDeDoc = (doc) => {
  const clave = normalizarClave(doc.clave)
  return {
    clave,
    nombre: doc.nombre || clave,
    cubeta: doc.cubeta || null,
    unidad: doc.unidad || 'dias',
    sintetico: false,
  }
}

/**
 * Que es esta entrada de incidencia.
 *
 * **Nunca devuelve null.** Una entrada que no se puede clasificar sale con `sintetico: true` y
 * `cubeta: null`, de modo que aparece en la rejilla con su clave en crudo en vez de perderse —
 * desaparecer seria subcontar en silencio.
 *
 * La entrada tiene doble identidad: `tipoIncidenciaId` es el camino bueno, pero es un ObjectId SIN
 * `ref` y hay entradas viejas que lo traen en null; en esas lo unico que queda es `tipo`, que unas
 * veces trae la CLAVE del catalogo ('FINJ') y otras un string semantico del motor ('vacacion').
 *
 * @returns {{clave, nombre, cubeta, unidad, sintetico}}
 */
export function resolverEntrada(entrada, indice) {
  const { porId, porClave, semanticos } = indice

  // 1. El camino bueno: el id.
  const id = entrada?.tipoIncidenciaId
  if (id) {
    const doc = porId.get(String(id))
    if (doc) return descriptorDeDoc(doc)
  }

  const bruto = typeof entrada?.tipo === 'string' ? entrada.tipo.trim() : ''

  // 2. La clave del catalogo guardada en `tipo` (el caso legado normal).
  const porClaveDoc = porClave.get(normalizarClave(bruto))
  if (porClaveDoc) return descriptorDeDoc(porClaveDoc)

  // 3. Un alias semantico que SI tiene documento en el catalogo.
  const alias = ALIAS_SEMANTICO[bruto.toLowerCase()]
  if (alias) {
    const doc = semanticos[bruto.toLowerCase()]
    if (doc) return descriptorDeDoc(doc)
    // 4. Alias conocido sin documento: se sabe la cubeta aunque no haya catalogo detras.
    return { ...alias, sintetico: true }
  }

  // 5. Ni id, ni clave, ni alias. Se muestra tal cual para que el usuario lo vea y lo corrija.
  const clave = normalizarClave(bruto) || 'S/T'
  return {
    clave,
    nombre: clave === 'S/T' ? 'Sin tipo de incidencia' : clave,
    cubeta: null,
    unidad: 'dias',
    sintetico: true,
  }
}

/**
 * Las columnas de dia del periodo: una por cada dia entre `fechaInicio` y `fechaFin`.
 *
 * `diaPeriodo` es la POSICION 1..N dentro del periodo, no el dia del mes — el dia 1 es
 * `fechaInicio`. `diaMes` y `etiqueta` son solo rotulos para el encabezado.
 *
 * Los septimos salen del snapshot de la Prenomina (`posisionSeptimoDia`, con la errata de origen).
 * Se marcan, no se bloquean: una incidencia capturada ahi no descuenta sueldo ni septimo dia, pero
 * SI cuenta para el ausentismo de IMSS.
 *
 * @returns {Array<{diaPeriodo, diaMes, etiqueta, esSeptimo}>}
 */
export function construirDiasPeriodo(prenomina) {
  const inicio = anclarDesdeUtc(prenomina?.fechaInicio)
  const fin = anclarDesdeUtc(prenomina?.fechaFin)
  if (inicio === null || fin === null) return []

  const n = Math.round((fin - inicio) / MS_DIA) + 1
  if (n <= 0) return []

  const septimos = new Set((prenomina?.posisionSeptimoDia || []).map(Number))

  return Array.from({ length: n }, (_, i) => {
    const d = new Date(inicio + i * MS_DIA)
    return {
      diaPeriodo: i + 1,
      diaMes: d.getUTCDate(),
      etiqueta: DIAS_SEMANA[d.getUTCDay()],
      esSeptimo: septimos.has(i + 1),
    }
  })
}

/**
 * Las entradas de incidencia que SI cuentan, indexadas por posicion del periodo, con las reglas 1
 * a 3 ya aplicadas. Es el filtro compartido de `rejillaDeLinea` y `resumenDiasDeLinea`: si cada
 * una filtrara por su cuenta, el TOTAL FALTAS de la Lista de Raya y los "Dias falta" de la hoja
 * plana podrian dejar de coincidir.
 *
 * La ventana es [posInicio, posFin] INCLUSIVA: el dia del alta y el de la baja se laboran y se
 * pagan. Ausentes ambos, se asume el periodo completo.
 *
 * @returns {{porDia: Map<number, Array>, posInicio: number, posFin: number}}
 */
function entradasEnVentana(linea) {
  const posInicio = Number(linea?.posInicioRelacion) || 1
  const posFinCrudo = Number(linea?.posFinRelacion)
  // OJO: `Number(null)` es 0 y 0 es un posFin valido (baja anterior al periodo), asi que solo un
  // valor ausente cae al periodo completo.
  const posFin = linea?.posFinRelacion != null && Number.isFinite(posFinCrudo) ? posFinCrudo : Infinity

  const porDia = new Map()
  for (const dia of linea?.incidencias || []) {
    const pos = Number(dia?.diaPeriodo)
    // Regla 1: `diaPeriodo < 1` es el respaldo sin posicion, no un dia.
    if (!Number.isFinite(pos) || pos < 1) continue
    // Regla 2: fuera de la relacion laboral no cuenta.
    if (pos < posInicio || pos > posFin) continue
    // Regla 3: `entradas: []` es "revisado, sin incidencia".
    if (!dia.entradas || dia.entradas.length === 0) continue
    porDia.set(pos, dia.entradas)
  }
  return { porDia, posInicio, posFin }
}

/**
 * Los dias que el empleado NO cobro en el periodo, por motivo. Alimenta la hoja plana "Datos por
 * empleado", que lee la Calculadora de Asimilables del cliente para descontar el proporcional.
 *
 * - `faltas`: la misma suma que el `totalFaltas` de `rejillaDeLinea` (los permisos sin goce ya
 *   viven en esa cubeta).
 * - `incapacidades`: dias de la cubeta 'incapacidades'. Van aparte porque el SAT y el IMSS los
 *   tratan distinto, aunque para el cliente tambien son dias que la empresa no paga.
 * - `fueraDeRelacion`: dias del periodo antes del alta o despues de la baja. Ahi no hay
 *   incidencias capturadas (la regla 2 las descarta), asi que se cuentan por posicion.
 * - `horasRetardo`: HORAS de la cubeta 'retardos' (su `cantidad` son horas, no dias). La
 *   calculadora del cliente las descuenta del presupuesto a razon de presupuesto / 7 / 8 por hora.
 *
 * @returns {{faltas: number, incapacidades: number, fueraDeRelacion: number, horasRetardo: number}}
 */
export function resumenDiasDeLinea(linea, dias, indice) {
  const { porDia, posInicio, posFin } = entradasEnVentana(linea)

  let faltas = 0
  let incapacidades = 0
  let fueraDeRelacion = 0
  let horasRetardo = 0
  for (const { diaPeriodo } of dias) {
    if (diaPeriodo < posInicio || diaPeriodo > posFin) {
      fueraDeRelacion += 1
      continue
    }
    for (const entrada of porDia.get(diaPeriodo) || []) {
      const tipo = resolverEntrada(entrada, indice)
      const cantidad = Number(entrada?.cantidad)
      const cant = Number.isFinite(cantidad) ? cantidad : 0
      if (tipo.cubeta === CUBETA_FALTAS) faltas += cant
      else if (tipo.cubeta === 'incapacidades' && tipo.unidad === 'dias') incapacidades += cant
      else if (tipo.cubeta === 'retardos') horasRetardo += cant
    }
  }
  return { faltas, incapacidades, fueraDeRelacion, horasRetardo }
}

// Marcas de la semana dia por dia en la hoja plana "Datos por empleado". Son CONTRATO con la
// Calculadora de Asimilables del cliente, que cuenta la rejilla por texto (COUNTIF): cambiarlas
// rompe su calculo.
export const MARCA_FUERA = '—'
export const MARCA_INCAPACIDAD = 'INC'
export const MARCA_VACACIONES = 'V'

/**
 * La semana de un empleado dia por dia, para la hoja plana. Es la rejilla de `rejillaDeLinea` con
 * tres diferencias, porque aqui la lee una macro y no una persona:
 *
 * - Los dias fuera de la relacion laboral (antes del alta, despues de la baja) se marcan con
 *   `MARCA_FUERA` en vez de quedar vacios: la calculadora los cuenta para descontarlos.
 * - Las incapacidades salen SIEMPRE como `MARCA_INCAPACIDAD`, sea cual sea su clave en el
 *   catalogo, y las vacaciones como `MARCA_VACACIONES` (es como las marca el cliente).
 * - Las faltas son NUMERO (la cantidad), igual que en la rejilla: la calculadora las suma.
 *
 * Por construccion: suma de numeros = `resumenDiasDeLinea().faltas`, cuantos `MARCA_FUERA` =
 * `fueraDeRelacion`, y cuantos `MARCA_INCAPACIDAD` = `incapacidades` cuando cada dia vale 1.
 * La unica excepcion es un dia con falta E incapacidad a la vez (dato contradictorio): aqui manda
 * la falta, para no descontar el dia dos veces, y el resumen cuenta las dos. La calculadora lo
 * detecta porque compara la rejilla contra los totales y avisa.
 *
 * @returns {Array<number|string>} una marca por dia de `dias` ('' = sin incidencia)
 */
export function marcasDiaDeLinea(linea, dias, indice) {
  const { porDia, posInicio, posFin } = entradasEnVentana(linea)
  return dias.map(({ diaPeriodo }) => {
    if (diaPeriodo < posInicio || diaPeriodo > posFin) return MARCA_FUERA
    const entradas = porDia.get(diaPeriodo)
    if (!entradas) return ''

    let faltas = 0
    let incapacidad = false
    let vacaciones = false
    const claves = []
    for (const entrada of entradas) {
      const tipo = resolverEntrada(entrada, indice)
      const cantidad = Number(entrada?.cantidad)
      const cant = Number.isFinite(cantidad) ? cantidad : 0
      if (tipo.cubeta === CUBETA_FALTAS) faltas += cant
      else if (tipo.cubeta === 'incapacidades' && tipo.unidad === 'dias') incapacidad = true
      else if (tipo.cubeta === 'vacaciones') vacaciones = true
      else if (tipo.clave && !claves.includes(tipo.clave)) claves.push(tipo.clave)
    }
    // Mismo orden de precedencia que la rejilla: la falta manda porque es lo que se descuenta.
    if (faltas > 0) return faltas
    if (incapacidad) return MARCA_INCAPACIDAD
    if (vacaciones) return MARCA_VACACIONES
    return claves.join('/')
  })
}

/**
 * La rejilla de un empleado: un marcador por dia del periodo y el total de faltas.
 *
 * Las dos salidas se derivan del MISMO recorrido a proposito: el total tiene que ser exactamente
 * la suma de los numeros que se ven en la fila, como el `=SUM()` del archivo del cliente. Si se
 * calcularan por separado, un dia guardado fuera de rango (`diaPeriodo > N`) las desalinearia.
 *
 * @param {Object} linea   linea de prenomina, con `incidencias[]`, `posInicioRelacion`, `posFinRelacion`
 * @param {Array}  dias    salida de `construirDiasPeriodo`
 * @param {Object} indice  salida de `construirIndiceTipos`
 * @returns {{celdas: Array<{valor: number|string, cubeta: string|null}|null>, totalFaltas: number}}
 */
export function rejillaDeLinea(linea, dias, indice) {
  const { porDia } = entradasEnVentana(linea)

  let totalFaltas = 0
  const celdas = dias.map(({ diaPeriodo }) => {
    const entradas = porDia.get(diaPeriodo)
    if (!entradas) return null

    let faltas = 0
    let cubetaOtra = null
    const claves = []

    for (const entrada of entradas) {
      const tipo = resolverEntrada(entrada, indice)
      const cantidad = Number(entrada?.cantidad)
      const cant = Number.isFinite(cantidad) ? cantidad : 0

      if (tipo.cubeta === CUBETA_FALTAS) {
        faltas += cant
      } else {
        if (!cubetaOtra) cubetaOtra = tipo.cubeta
        if (tipo.clave && !claves.includes(tipo.clave)) claves.push(tipo.clave)
      }
    }

    // Regla 4: la falta manda y va como numero, porque es lo que el TOTAL suma. Lo demas se rotula
    // con su clave — la misma abreviatura que ya usa la rejilla en pantalla.
    if (faltas > 0) {
      totalFaltas += faltas
      return { valor: faltas, cubeta: CUBETA_FALTAS }
    }
    if (claves.length) return { valor: claves.join('/'), cubeta: cubetaOtra }
    return null
  })

  return { celdas, totalFaltas }
}
